// web/src/pages/Privacy.tsx
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE_TEXT = "August 21, 2025";
const LAST_UPDATED_TEXT = "August 21, 2025";
const SUPPORT_EMAIL = "support@spellgather.com";

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-8 mb-3">
        {title}
      </h2>
      <div className="prose prose-invert max-w-none">{children}</div>
    </section>
  );
}

export default function Privacy(): JSX.Element {
  return (
    <main className="mx-auto max-w-4xl p-6">
      <Card className="bg-gray-900 border-gray-700 shadow-xl">
        <CardHeader>
          <CardTitle className="text-white">Privacy Policy</CardTitle>
          <CardDescription className="text-white/90">
            Effective: {EFFECTIVE_DATE_TEXT} · Last Updated: {LAST_UPDATED_TEXT}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-white">
          {/* Quick links */}
          <nav aria-label="Table of contents" className="mb-6">
            <ul className="text-sm grid sm:grid-cols-2 gap-2">
              <li>
                <a className="underline" href="#scope">
                  1. Scope
                </a>
              </li>
              <li>
                <a className="underline" href="#info-we-collect">
                  2. Information We Collect
                </a>
              </li>
              <li>
                <a className="underline" href="#sources">
                  3. Sources of Information
                </a>
              </li>
              <li>
                <a className="underline" href="#use">
                  4. How We Use Information
                </a>
              </li>
              <li>
                <a className="underline" href="#legal-bases">
                  5. Legal Bases (EEA/UK)
                </a>
              </li>
              <li>
                <a className="underline" href="#cookies">
                  6. Cookies & Similar Technologies
                </a>
              </li>
              <li>
                <a className="underline" href="#sharing">
                  7. How We Share Information
                </a>
              </li>
              <li>
                <a className="underline" href="#children">
                  8. Children’s Privacy
                </a>
              </li>
              <li>
                <a className="underline" href="#security">
                  9. Data Security
                </a>
              </li>
              <li>
                <a className="underline" href="#retention">
                  10. Data Retention
                </a>
              </li>
              <li>
                <a className="underline" href="#transfers">
                  11. International Transfers
                </a>
              </li>
              <li>
                <a className="underline" href="#your-rights">
                  12. Your Privacy Rights
                </a>
              </li>
              <li>
                <a className="underline" href="#exercising">
                  13. How to Exercise Your Rights
                </a>
              </li>
              <li>
                <a className="underline" href="#dnt">
                  14. “Do Not Track”
                </a>
              </li>
              <li>
                <a className="underline" href="#changes">
                  15. Changes to this Policy
                </a>
              </li>
              <li>
                <a className="underline" href="#contact">
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          <Section id="scope" title="1) Scope">
            <p>
              This Privacy Policy explains how Spellgather (“we,” “our,” or
              “us”) collects, uses, shares, and protects personal information
              when you use our website and services (collectively, the
              “Service”). This Policy applies to all users, including parents/
              guardians supervising teen users (13–17). By using the Service,
              you agree to the practices described here.
            </p>
            <p>
              This Privacy Policy is part of our{" "}
              <Link to="/terms" className="underline">
                Terms of Service
              </Link>{" "}
              and supported by our{" "}
              <Link to="/data-protection" className="underline">
                Data Sharing & Protection Statement
              </Link>
              .
            </p>
          </Section>

          <Section id="info-we-collect" title="2) Information We Collect">
            <p>We collect the following categories of information:</p>
            <ul>
              <li>
                <strong>Account & Profile:</strong> username, display name,
                avatar, bio, city/state (if you choose to display it),
                play/trade preferences, play formats, Commander/EDH brackets,
                meetup preferences, preferred LGS, visibility settings, and
                availability toggles.
              </li>
              <li>
                <strong>Contact:</strong> email address for account
                administration and optional notifications (e.g., trade/purchase
                offers, follows, messages).
              </li>
              <li>
                <strong>Content:</strong> collection entries (cards you add),
                wishlist items, ratings/notes you create, offers you
                send/receive, and messages you exchange.
              </li>
              <li>
                <strong>Device & Usage:</strong> IP address, browser type,
                device identifiers, session activity, pages viewed, and
                referring URLs (used for security, analytics, and improving the
                Service).
              </li>
              <li>
                <strong>Location (optional):</strong> approximate location if
                you explicitly opt in (e.g., to find nearby stores). You can
                turn this off at any time in Settings.
              </li>
              <li>
                <strong>Transaction context:</strong> non-sensitive history
                markers for items within the Service (e.g., “added to
                collection,” “traded/sold to @username,” with timestamps), for
                transparency and trust among users.
              </li>
            </ul>
          </Section>

          <Section id="sources" title="3) Sources of Information">
            <ul>
              <li>
                <strong>Directly from you:</strong> when you create an account,
                update a profile, add items, send messages, or change settings.
              </li>
              <li>
                <strong>Automatically:</strong> via cookies, logs, and similar
                technologies when you use the Service.
              </li>
              <li>
                <strong>From others:</strong> other users may reference your
                username in offers, messages, or item history within the
                platform.
              </li>
              <li>
                <strong>Service providers:</strong> analytics or infrastructure
                partners may provide aggregated or pseudonymous metrics.
              </li>
            </ul>
          </Section>

          <Section id="use" title="4) How We Use Information">
            <ul>
              <li>Provide, operate, secure, and troubleshoot the Service.</li>
              <li>
                Enable core features: profiles, collections, wishlists, offers,
                messages, followers, and discovery.
              </li>
              <li>
                Send administrative messages (e.g., account notices, security
                alerts).
              </li>
              <li>
                Send optional email notifications (e.g., offers, follows, direct
                messages) if you opt in.
              </li>
              <li>
                Improve and personalize the Service (including layout, search,
                and recommendations).
              </li>
              <li>
                Enforce our{" "}
                <Link to="/terms" className="underline">
                  Terms
                </Link>
                , prevent fraud, and protect users.
              </li>
              <li>
                Comply with legal obligations and respond to lawful requests.
              </li>
            </ul>
          </Section>

          <Section id="legal-bases" title="5) Legal Bases (EEA/UK)">
            <p>
              If you are in the EEA/UK, we process personal data under these
              bases:
            </p>
            <ul>
              <li>
                <strong>Contract:</strong> to provide and support the Service
                you request.
              </li>
              <li>
                <strong>Legitimate Interests:</strong> to secure and improve the
                Service, prevent abuse, and support user-to-user trust.
              </li>
              <li>
                <strong>Consent:</strong> for optional notifications,
                location-based suggestions, and certain cookies/analytics (where
                required).
              </li>
              <li>
                <strong>Legal Obligation:</strong> to comply with applicable
                laws and requests.
              </li>
            </ul>
          </Section>

          <Section id="cookies" title="6) Cookies & Similar Technologies">
            <p>
              We use cookies and similar technologies for authentication,
              security, preferences, and analytics. You can manage your browser
              cookie settings, but essential cookies are required for core
              functionality. Where required by law, we will obtain consent for
              non-essential cookies.
            </p>
          </Section>

          <Section id="sharing" title="7) How We Share Information">
            <ul>
              <li>
                <strong>Service Providers:</strong> We share personal
                information with vendors who help us operate the Service (e.g.,
                hosting, storage, analytics, email delivery) under contracts
                that limit their use of your information.
              </li>
              <li>
                <strong>Community Visibility:</strong> Your profile, collection
                visibility, wishlist, and availability are controlled by your
                Settings. Content you choose to make public may be visible to
                others inside the Service.
              </li>
              <li>
                <strong>Legal & Safety:</strong> We may disclose information to
                comply with law, enforce our Terms, or protect the rights,
                safety, and property of users and the community.
              </li>
              <li>
                <strong>Business Transfers:</strong> If we are involved in a
                merger, acquisition, or asset sale, your information may be
                transferred as part of that transaction.
              </li>
            </ul>
            <p className="mt-2">
              We do <strong>not</strong> sell personal information. We do not
              share personal information for cross-context behavioral
              advertising without your consent where required by law.
            </p>
          </Section>

          <Section id="children" title="8) Children’s Privacy">
            <p>
              The Service is intended for users aged 13 and over. Users aged
              13–17 may use the Service only with the consent and supervision of
              a parent or legal guardian, who must review and accept the{" "}
              <Link to="/terms" className="underline">
                Terms
              </Link>{" "}
              and this Privacy Policy. If you believe a child under 13 has
              provided personal information, please contact{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>{" "}
              so we can take appropriate action.
            </p>
          </Section>

          <Section id="security" title="9) Data Security">
            <p>
              We use reasonable administrative, technical, and physical
              safeguards designed to protect personal information. No method of
              transmission or storage is 100% secure. You are responsible for
              safeguarding your account credentials.
            </p>
          </Section>

          <Section id="retention" title="10) Data Retention">
            <p>
              We retain personal information for as long as necessary to provide
              the Service, comply with legal obligations, resolve disputes, and
              enforce agreements. Retention periods vary based on the type of
              data and our legal/operational needs. You may delete certain
              content at any time (e.g., wishlist entries, some profile fields)
              via Settings; system logs and legal copies may persist for a
              limited period.
            </p>
          </Section>

          <Section id="transfers" title="11) International Transfers">
            <p>
              If you access the Service from outside the country where our
              servers or providers are located, your information may be
              transferred internationally. Where required, we rely on
              appropriate safeguards (such as standard contractual clauses) for
              cross-border transfers.
            </p>
          </Section>

          <Section id="your-rights" title="12) Your Privacy Rights">
            <p>
              Depending on your location, you may have rights to access,
              correct, delete, or export your personal information, to object to
              or restrict certain processing, and to withdraw consent where
              processing is based on consent.
            </p>
            <ul>
              <li>
                <strong>Access & Portability:</strong> request a copy of your
                personal data.
              </li>
              <li>
                <strong>Correction:</strong> update inaccurate or incomplete
                information.
              </li>
              <li>
                <strong>Deletion:</strong> request deletion of data subject to
                legal exceptions.
              </li>
              <li>
                <strong>Objection/Restriction:</strong> object to or request
                restriction of certain processing.
              </li>
              <li>
                <strong>Consent Withdrawal:</strong> withdraw consent for
                optional features (e.g., email notifications, location
                suggestions) in Settings.
              </li>
            </ul>
            <p className="mt-2">
              <strong>US State Privacy Laws (e.g., CA/CO/CT/VA/UT):</strong> You
              may have additional rights to access, delete, correct, opt out of
              targeted advertising, and limit use of sensitive personal
              information (we do not use sensitive categories for advertising).
              We do not sell your personal information. If we deny a request in
              whole or part, you may have the right to appeal; instructions will
              be provided in our response.
            </p>
          </Section>

          <Section id="exercising" title="13) How to Exercise Your Rights">
            <p>
              You can manage many privacy choices in{" "}
              <Link to="/settings" className="underline">
                Settings
              </Link>{" "}
              (e.g., visibility, email preferences, location, public presence).
              To submit a privacy request (access, deletion, correction,
              portability, or appeal), contact us at{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              . We may need to verify your identity before fulfilling requests.
              Authorized agents may submit requests on your behalf where
              permitted by law.
            </p>
            <div className="mt-4 flex gap-2">
              <Link to="/settings">
                <Button className="bg-purple-600 hover:bg-purple-700">
                  Manage Privacy Settings
                </Button>
              </Link>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Privacy%20Request`}>
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-800"
                >
                  Email a Privacy Request
                </Button>
              </a>
            </div>
          </Section>

          <Section id="dnt" title="14) “Do Not Track”">
            <p>
              Some browsers transmit “Do Not Track” signals. Because there is no
              industry consensus on how to respond, we do not currently act on
              DNT signals. We will update this Policy if that changes.
            </p>
          </Section>

          <Section id="changes" title="15) Changes to this Policy">
            <p>
              We may update this Privacy Policy to reflect changes to our
              practices or the law. If we make material changes, we will provide
              notice (e.g., in-app notice or email). The updated Policy will be
              effective on the stated date. Your continued use of the Service
              after the effective date means you accept the changes.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Questions about privacy? Contact{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <p className="mt-2">
              See also our{" "}
              <Link to="/data-protection" className="underline">
                Data Sharing & Protection Statement
              </Link>{" "}
              and{" "}
              <Link to="/terms" className="underline">
                Terms of Service
              </Link>
              .
            </p>
          </Section>

          {/* Convenience CTA back to the acknowledgement flow */}
          <div className="mt-8">
            <Link to="/terms-ack?next=/settings">
              <Button className="bg-purple-600 hover:bg-purple-700">
                Return to Acknowledgement
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

